import type { JSONValue } from '@ai-sdk/ui-utils';
import { applyPatch } from 'diff';
import { htmlEscape } from 'escape-goat';
import { sendAnalyticsEvent } from '~/lib/analytics.client';
import { waitForProjectImport } from '~/lib/projects';
import { ShellAction, StartAction } from '~/lib/runtime/actions';
import { analysisWatcher } from '~/lib/static-analysis/watcher';
import { getDescription } from '~/lib/stores/description';
import { workbenchStore } from '~/lib/stores/workbench';
import { AnalyticsAction, AnalyticsTrackEvent } from '~/types/analytics';
import { logger } from '~/utils/logger';
import { addWorkDir } from '~/utils/workDir';

export async function applyAnnotations(annotations: JSONValue[] | undefined) {
  if (annotations) {
    await waitForProjectImport();

    // we flatten all file writes to ensure they're only applied once
    const temporaryFiles = new Map<string, string>();

    const deletedFiles = new Set<string>();

    // we use a set to collect all shell commands making sure we don't execute the same command multiple times
    const commands = new Set<string>();

    for (const annotation of annotations) {
      if (typeof annotation === 'object' && annotation && !Array.isArray(annotation)) {
        if (annotation.type === 'file_event') {
          const { filePath, content } = annotation;

          if (typeof filePath !== 'string') {
            continue;
          }

          if (annotation.eventType === 'delete') {
            temporaryFiles.delete(filePath);

            deletedFiles.add(filePath);
          }

          if (typeof content !== 'string') {
            continue;
          }

          if (annotation.eventType === 'create') {
            deletedFiles.delete(filePath);

            temporaryFiles.set(filePath, content);
          } else if (annotation.eventType === 'edit') {
            if (deletedFiles.has(filePath)) {
              // we should not receive an edit of a deleted file
              logger.error(`CC edit of deleted file`, {
                filePath,
                diff: content,
              });

              continue;
            }

            let fileContent = temporaryFiles.get(filePath);

            if (!fileContent) {
              try {
                fileContent = workbenchStore.readFile(filePath).content;
              } catch {
                // in case the file does not exist on local, let's try to assume a new file with empty content
                fileContent = '';
              }

              temporaryFiles.set(filePath, fileContent);
            }

            const patched = applyPatch(fileContent, content);

            if (patched) {
              temporaryFiles.set(filePath, patched);
            } else {
              logger.error(`CC diff failed to apply`, {
                original: fileContent,
                diff: content,
              });

              sendAnalyticsEvent({
                action: AnalyticsAction.Track,
                payload: {
                  event: AnalyticsTrackEvent.ClaudeCodeServerInvalidDiff,
                  properties: {
                    original: fileContent,
                    diff: content,
                  },
                },
              });
            }
          }
        } else if (annotation.type === 'shell_command') {
          if (typeof annotation.command !== 'string') {
            // skip invalid annotations
            continue;
          }

          const command = annotation.command;

          commands.add(command);
        }
      }
    }

    let fileChain = Promise.resolve();

    for (const filePath of deletedFiles) {
      fileChain = fileChain.then(() => workbenchStore.removeFile(filePath));
    }

    for (const [filePath, content] of temporaryFiles) {
      const fullFilePath = addWorkDir(filePath);

      analysisWatcher.queueAdd(fullFilePath);

      fileChain = fileChain.then(() => {
        return workbenchStore.writeFile(filePath, { type: 'file', content }).catch(() => {
          analysisWatcher.queueRemove(fullFilePath);
        });
      });
    }

    await fileChain;

    try {
      // apply title change
      const description = getDescription();
      const indexHtml = workbenchStore.readFile('index.html');

      if (description && indexHtml.type === 'file') {
        const replacePattern = /<title data-default>(.*?)</;

        if (replacePattern.test(indexHtml.content)) {
          await workbenchStore.writeFile('index.html', {
            type: 'file',
            content: indexHtml.content.replace(replacePattern, `<title>${htmlEscape(description)}<`),
          });
        }
      }
    } catch {
      // ignore if the file does not exist or if something goes wrong
    }

    if (commands.size > 0) {
      // execute all shell commands in order
      for (const command of commands) {
        await new ShellAction('', '', '', command).execute();
      }

      // we execute the start action but don't wait for it
      new StartAction('', '', '', false, workbenchStore.framework.get()).execute();
    }
  }
}
